import { useEffect, useState } from "react";
import {
  getListings, createListing, updateListing,
  deleteListing, getAllInterests, respondToInterest
} from "../../firebase/services";
import { Badge, EmptyState, Spinner, Modal, Btn, Input, Textarea } from "../../components/UI";

const STATUS_MAP = {
  available: { label: "Disponible",        color: "#2d6a0a", bg: "#e8f5e0", icon: "🟢" },
  soon:      { label: "Bientôt disponible", color: "#8a4c00", bg: "#fff3e0", icon: "🟡" },
  closed:    { label: "Fermée",             color: "#5a7a9a", bg: "#f0f4f8", icon: "⚫" },
};

function timeAgo(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.round(s / 3600)}h`;
  return `il y a ${Math.round(s / 86400)}j`;
}

export default function OwnerListings() {
  const [listings, setListings] = useState([]);
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [interestModal, setInterestModal] = useState(null); // listing object
  const [filter, setFilter] = useState("all");
  const [responding, setResponding] = useState(null);

  // Form state
  const [form, setForm] = useState({
    title: "", description: "", price: "27500",
    roomNumber: "", availableFrom: "", status: "available"
  });

  useEffect(() => {
    const unsubs = [
      getListings(data => { setListings(data); setLoading(false); }),
      getAllInterests(setInterests),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  const openNew = () => {
    setForm({ title: "", description: "", price: "27500", roomNumber: "", availableFrom: "", status: "available" });
    setEditTarget(null);
    setShowForm(true);
  };

  const openEdit = (listing) => {
    setForm({
      title: listing.title || "",
      description: listing.description || "",
      price: String(listing.price || 27500),
      roomNumber: listing.roomNumber || "",
      availableFrom: listing.availableFrom || "",
      status: listing.status || "available"
    });
    setEditTarget(listing);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.roomNumber.trim()) return;
    const data = {
      title: form.title.trim(),
      description: form.description.trim(),
      price: parseInt(form.price) || 27500,
      roomNumber: form.roomNumber.trim(),
      availableFrom: form.availableFrom,
      status: form.status,
    };
    if (editTarget) {
      await updateListing(editTarget.id, data);
    } else {
      await createListing({ ...data, photos: [] });
    }
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette annonce ?")) {
      await deleteListing(id);
    }
  };

  const handleRespond = async (interestId, status) => {
    setResponding(interestId);
    await respondToInterest(interestId, status);
    setResponding(null);
  };

  const interestsFor = (listingId) =>
    interests.filter(i => i.listingId === listingId);

  const pendingCount = interests.filter(i => i.status === "pending").length;

  const filtered = listings.filter(l => {
    if (filter === "active") return l.status !== "closed";
    if (filter === "interest") return interestsFor(l.id).some(i => i.status === "pending");
    return true;
  });

  if (loading) return <div style={styles.page}><Spinner /></div>;

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <h1 style={styles.title}>Annonces</h1>
          {pendingCount > 0 && (
            <span style={styles.pendingChip}>🔔 {pendingCount} intérêt{pendingCount > 1 ? "s" : ""} en attente</span>
          )}
        </div>
        <button style={styles.addBtn} onClick={openNew}>+ Nouvelle annonce</button>
      </div>

      <div style={styles.filterBar}>
        {[["all","Toutes"],["active","Actives"],["interest","Avec intérêts"]].map(([val, label]) => (
          <button
            key={val}
            style={{ ...styles.pill, ...(filter === val ? styles.pillActive : {}) }}
            onClick={() => setFilter(val)}
          >{label}</button>
        ))}
      </div>

      <div style={styles.scroll}>
        {filtered.length === 0 ? (
          <EmptyState icon="🏠" title="Aucune annonce" subtitle="Créez une annonce pour une chambre disponible" />
        ) : filtered.map(listing => {
          const st = STATUS_MAP[listing.status] || STATUS_MAP.available;
          const iList = interestsFor(listing.id);
          const pending = iList.filter(i => i.status === "pending");

          return (
            <div key={listing.id} style={styles.listingCard}>
              {/* Header */}
              <div style={styles.listingHeader}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 12 }}>{st.icon}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: st.color }}>{st.label}</span>
                    <span style={styles.roomChip}>Ch. {listing.roomNumber}</span>
                  </div>
                  <p style={styles.listingTitle}>{listing.title}</p>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={styles.iconBtn} onClick={() => openEdit(listing)} aria-label="Modifier">✏️</button>
                  <button style={styles.iconBtn} onClick={() => handleDelete(listing.id)} aria-label="Supprimer">🗑️</button>
                </div>
              </div>

              {/* Description */}
              {listing.description && (
                <p style={styles.listingDesc}>{listing.description}</p>
              )}

              {/* Details row */}
              <div style={styles.detailsRow}>
                <span style={styles.priceTag}>
                  {(listing.price || 27500).toLocaleString("fr-FR")} FCFA/mois
                </span>
                {listing.availableFrom && (
                  <span style={styles.dateTag}>📅 Dispo le {listing.availableFrom}</span>
                )}
              </div>

              {/* Interests */}
              <div style={styles.interestRow}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={styles.interestCount}>
                    👥 {iList.length} intérêt{iList.length > 1 ? "s" : ""}
                  </span>
                  {pending.length > 0 && (
                    <span style={styles.pendingBadge}>{pending.length} en attente</span>
                  )}
                </div>
                {iList.length > 0 && (
                  <button style={styles.viewInterestsBtn} onClick={() => setInterestModal(listing)}>
                    Voir les candidats →
                  </button>
                )}
              </div>

              {/* Quick status toggle */}
              <div style={styles.statusToggle}>
                {Object.entries(STATUS_MAP).map(([key, val]) => (
                  <button
                    key={key}
                    style={{
                      ...styles.statusPill,
                      ...(listing.status === key ? { background: val.bg, color: val.color, fontWeight: 700 } : {})
                    }}
                    onClick={() => updateListing(listing.id, { status: key })}
                  >{val.icon} {val.label}</button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create / Edit modal */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editTarget ? "Modifier l'annonce" : "Nouvelle annonce"}
      >
        <div>
          <Input
            label="Titre de l'annonce"
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Ex: Chambre meublée, lumineuse"
          />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <Input
                label="N° de chambre"
                value={form.roomNumber}
                onChange={e => setForm(f => ({ ...f, roomNumber: e.target.value }))}
                placeholder="101"
              />
            </div>
            <div style={{ flex: 1 }}>
              <Input
                label="Loyer (FCFA)"
                type="number"
                value={form.price}
                onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
              />
            </div>
          </div>
          <Textarea
            label="Description (équipements, conditions...)"
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Chambre avec lit, bureau, armoire. Eau incluse..."
          />
          <Input
            label="Disponible à partir du"
            type="date"
            value={form.availableFrom}
            onChange={e => setForm(f => ({ ...f, availableFrom: e.target.value }))}
          />
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, color: "#4a6a8a", marginBottom: 6, fontWeight: 500 }}>Statut</p>
            <div style={{ display: "flex", gap: 8 }}>
              {Object.entries(STATUS_MAP).map(([key, val]) => (
                <button
                  key={key}
                  style={{
                    ...styles.statusPill,
                    flex: 1,
                    ...(form.status === key ? { background: val.bg, color: val.color, fontWeight: 700 } : {})
                  }}
                  onClick={() => setForm(f => ({ ...f, status: key }))}
                >{val.icon} {val.label}</button>
              ))}
            </div>
          </div>
          <Btn
            variant="primary"
            onClick={handleSubmit}
            disabled={!form.title.trim() || !form.roomNumber.trim()}
          >
            {editTarget ? "Enregistrer les modifications" : "Publier l'annonce"}
          </Btn>
        </div>
      </Modal>

      {/* Interests modal */}
      <Modal
        open={!!interestModal}
        onClose={() => setInterestModal(null)}
        title={`Candidats — ${interestModal?.title || ""}`}
      >
        {interestModal && (() => {
          const iList = interestsFor(interestModal.id);
          return iList.length === 0 ? (
            <EmptyState icon="👥" title="Aucun candidat" subtitle="Personne n'a encore manifesté d'intérêt" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {iList.map(interest => {
                const statusStyle = {
                  pending:  { bg: "#fff9e6", color: "#8a6a00", label: "En attente" },
                  accepted: { bg: "#e8f5e0", color: "#2d6a0a", label: "Accepté ✓" },
                  refused:  { bg: "#fdecea", color: "#a32d2d", label: "Refusé" },
                };
                const ss = statusStyle[interest.status] || statusStyle.pending;
                return (
                  <div key={interest.id} style={styles.candidateCard}>
                    <div style={styles.candidateHeader}>
                      <div style={styles.candidateAvatar}>
                        {(interest.userName || "?").charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={styles.candidateName}>{interest.userName || "Inconnu"}</p>
                        <p style={styles.candidateTime}>{timeAgo(interest.createdAt)}</p>
                      </div>
                      <span style={{ ...styles.candidateStatus, background: ss.bg, color: ss.color }}>
                        {ss.label}
                      </span>
                    </div>
                    {interest.message && (
                      <p style={styles.candidateMsg}>"{interest.message}"</p>
                    )}
                    {interest.status === "pending" && (
                      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                        <button
                          style={styles.acceptBtn}
                          onClick={() => handleRespond(interest.id, "accepted")}
                          disabled={responding === interest.id}
                        >✓ Accepter</button>
                        <button
                          style={styles.refuseBtn}
                          onClick={() => handleRespond(interest.id, "refused")}
                          disabled={responding === interest.id}
                        >✕ Refuser</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}

const styles = {
  page: { display: "flex", flexDirection: "column", height: "100%", background: "#f5f8fc", fontFamily: "'Barlow', sans-serif" },
  topBar: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "14px 16px 8px" },
  title: { fontSize: 22, fontWeight: 800, color: "#1a3a5c", fontFamily: "'Barlow Condensed', sans-serif", marginBottom: 2 },
  pendingChip: { fontSize: 11, color: "#8a4c00", background: "#fff3e0", borderRadius: 20, padding: "2px 8px", fontWeight: 600 },
  addBtn: { background: "#1a3a5c", color: "white", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" },
  filterBar: { display: "flex", gap: 8, padding: "0 16px 10px", overflowX: "auto" },
  pill: { padding: "6px 14px", borderRadius: 20, border: "1.5px solid #d0dce8", background: "white", color: "#5a7a9a", fontSize: 12, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" },
  pillActive: { background: "#1a3a5c", color: "white", borderColor: "#1a3a5c" },
  scroll: { flex: 1, overflowY: "auto", padding: "0 16px 80px" },
  listingCard: { background: "white", borderRadius: 14, padding: "14px", marginBottom: 12, border: "0.5px solid #d0dce8" },
  listingHeader: { display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 },
  roomChip: { fontSize: 11, background: "#e3f1fb", color: "#1a3a5c", borderRadius: 20, padding: "1px 8px", fontWeight: 600 },
  listingTitle: { fontSize: 15, fontWeight: 700, color: "#1a2a3a", margin: 0 },
  listingDesc: { fontSize: 13, color: "#4a6a8a", lineHeight: 1.5, margin: "0 0 10px" },
  detailsRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 10 },
  priceTag: { fontSize: 14, fontWeight: 800, color: "#1a3a5c", fontFamily: "'Barlow Condensed', sans-serif" },
  dateTag: { fontSize: 12, color: "#7a90a8" },
  interestRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  interestCount: { fontSize: 12, color: "#5a7a9a" },
  pendingBadge: { fontSize: 11, background: "#fff3e0", color: "#8a4c00", borderRadius: 20, padding: "1px 8px", fontWeight: 600 },
  viewInterestsBtn: { fontSize: 12, color: "#2e86c1", background: "none", border: "none", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" },
  statusToggle: { display: "flex", gap: 6, marginTop: 4 },
  statusPill: { flex: 1, padding: "5px 4px", borderRadius: 8, border: "1px solid #d0dce8", background: "#f5f8fc", color: "#7a90a8", fontSize: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "center" },
  iconBtn: { background: "none", border: "none", fontSize: 16, cursor: "pointer", padding: "2px 4px" },
  candidateCard: { background: "#f7fafd", borderRadius: 12, padding: "12px" },
  candidateHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 },
  candidateAvatar: { width: 34, height: 34, borderRadius: "50%", background: "#e3f1fb", color: "#1a3a5c", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700, flexShrink: 0 },
  candidateName: { fontSize: 13, fontWeight: 600, color: "#1a2a3a", margin: 0 },
  candidateTime: { fontSize: 11, color: "#7a90a8", margin: 0 },
  candidateStatus: { fontSize: 11, fontWeight: 600, borderRadius: 20, padding: "2px 9px" },
  candidateMsg: { fontSize: 12, color: "#4a6a8a", fontStyle: "italic", margin: "4px 0 0", paddingLeft: 4, borderLeft: "2px solid #d0dce8" },
  acceptBtn: { flex: 1, padding: "8px", borderRadius: 8, background: "#e8f5e0", color: "#2d6a0a", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" },
  refuseBtn: { flex: 1, padding: "8px", borderRadius: 8, background: "#fdecea", color: "#a32d2d", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" },
};
